import type { DocArticle } from './documents.ts';

/** What a client is handed: the report, what changed, and the period view. */

const REPORTS: DocArticle = {
  id: 'reports',
  title: 'The report',
  summary: 'What the document contains, how to produce a PDF, and how branding works.',
  sections: [
    {
      id: 'shape',
      heading: 'What is in it',
      paragraphs: [
        'A report is rendered once, when the assessment runs, and stored. Opening it later serves the same bytes the client was sent — it is a historical artefact, not a live view.',
        'It opens with what the document is and is not, then the tenant and scan details, the licences held and capabilities detected, and — where there is history — what changed since the last assessment and progress over recent ones. Then the summary counts, coverage by framework criterion, the controls not met, the open questions, and the controls met or not applicable.',
      ],
    },
    {
      id: 'appendices',
      heading: 'The appendices are the audit trail',
      paragraphs: [
        'Appendix A is the collection log: every configuration read, where it was read from, what it is for, the outcome and how many records came back. Successful reads are listed too, not only failures, because a reader needs to see what was looked at.',
        'Appendix B is control traceability: for each control, the verdict, the rule version that decided it, the evidence, the sources read and the thresholds applied. Between them, a reader can go from any verdict back to the data that produced it.',
      ],
    },
    {
      id: 'pdf',
      heading: 'Producing a PDF',
      paragraphs: [
        'Save as PDF opens the report in a new tab and asks your browser to print it; choose “Save as PDF” as the destination. There is no server-side PDF engine, which means what a client receives is what you saw.',
        'If nothing happens, your browser blocked the new tab. The console says so when it detects it.',
      ],
    },
    {
      id: 'branding',
      heading: 'Branding',
      paragraphs: [
        'Settings → Report branding sets your organisation name, logo, brand colour and footer line. These style the reports your clients receive, not this console.',
        'Branding applies to reports generated from now on. A report already sent to a client is not restyled, because it has already been read and filed.',
        'Branding belongs to your account, not to an individual client: one identity across every report you produce. Branding can never remove a caveat from a report.',
      ],
    },
  ],
};

const DRIFT: DocArticle = {
  id: 'drift',
  title: 'What changed since last time',
  summary: 'Regressions, improvements, and the differences that are ours rather than the client’s.',
  sections: [
    {
      id: 'drift-basics',
      heading: 'What it compares',
      paragraphs: [
        'Drift compares an assessment with the previous completed one, using the findings as they were stored. It never re-evaluates the old configuration with today’s rules, because a client must be able to diff the report they were actually sent.',
        'A client’s first assessment has nothing to compare with, and says so.',
      ],
    },
    {
      id: 'drift-kinds',
      heading: 'The kinds of change',
      paragraphs: ['Five figures head the Overview, and the full list is on the Drift tab.'],
      bullets: [
        'Regressed — met at the previous assessment, not met now.',
        'Improved — not met before, met now.',
        'Changed, still met — same verdict, different configuration underneath. The early warning.',
        'No longer visible — we could assess it before and cannot now. Not a regression.',
        'Not comparable — our own rules, thresholds or collection changed between the two, so any difference may be ours.',
      ],
    },
    {
      id: 'visibility',
      heading: 'Losing sight of something is not a regression',
      paragraphs: [
        'A control we can no longer assess — a licence lapsing, consent narrowed — is reported as a visibility change, in the colour of an open question and never in red. Nothing about it says the client got worse, only that we stopped being able to see it.',
        'The same care applies to our own changes. When a rule version or a threshold moved between two assessments, the two sides are not measuring the same thing, and the console says why rather than presenting a difference the client did not cause.',
      ],
    },
    {
      id: 'identical',
      heading: 'When nothing changed',
      paragraphs: [
        'If two collections hash identically, the product says so plainly: the client’s configuration is byte-for-byte the same as it was. That is a useful thing to be able to tell a client, and it is stronger than “no differences found”.',
      ],
    },
  ],
};

const PROGRESS: DocArticle = {
  id: 'progress',
  title: 'Progress and service reviews',
  summary: 'The longer view, and the document you hand a client at the review meeting.',
  sections: [
    {
      id: 'progress',
      heading: 'Progress',
      paragraphs: [
        'The Progress tab shows each control across recent assessments, oldest first, with a mark per assessment: met, not met, not evidenced, not applicable, a gap where the control produced no finding, and a break drawn between two assessments our own change made incomparable.',
        'Three figures sit above it: what was put right in the period, what was seen to start failing, and how much history a change of ours has broken. Where a count cannot honestly be given it is shown as a dash rather than as zero — a withheld number is not the same as none.',
      ],
    },
    {
      id: 'service-review',
      heading: 'Service reviews',
      paragraphs: [
        'A service review is the document you hand a client at the review meeting. Over a period you name, it sets out what was put right, what regressed, what is still open and for how long, and what could not be assessed.',
        'It is built from assessments already completed. Producing one reads nothing from the client’s tenant and changes nothing.',
        'Both dates are inside the period. Presets cover the last complete quarter — never the running one — and the last ninety days.',
      ],
    },
    {
      id: 'empty-periods',
      heading: 'A period with no assessments',
      paragraphs: [
        'If nothing was assessed in the period you named, the review says so and reports nothing else. That is a document worth sending when the question is whether the client was assessed — but it is not a quiet quarter, and it must not be read as one.',
      ],
    },
    {
      id: 'how-long',
      heading: 'How long something has been failing',
      paragraphs: [
        'A run of failures is measured from the first assessment that saw it. Where that run starts at the edge of the window, or just after an assessment we could not make, the product qualifies the duration instead of implying we watched the whole time. Those sentences are there to stop a report claiming more precision than the evidence supports.',
      ],
    },
  ],
};

export const REPORTING: readonly DocArticle[] = [REPORTS, DRIFT, PROGRESS];
